import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, X, GripVertical, ChevronDown, ChevronUp, Box, Server, Shield, Globe, Key, Cloud, Wifi, HardDrive, Phone, FileText, Router, Mail, Layers, Lock, Building2, Settings, Check, AlertTriangle, Copy, Download, Eye } from 'lucide-react';
import { documentationAPI } from '../../services/api';
import { ASSET_TEMPLATES, ASSET_TEMPLATE_CATEGORIES } from '../../constants/assetTemplates';

// Available icons for asset types
const AVAILABLE_ICONS = [
  { name: 'box', icon: Box, label: 'Box' },
  { name: 'server', icon: Server, label: 'Server' },
  { name: 'shield', icon: Shield, label: 'Shield' },
  { name: 'globe', icon: Globe, label: 'Globe' },
  { name: 'key', icon: Key, label: 'Key' },
  { name: 'cloud', icon: Cloud, label: 'Cloud' },
  { name: 'wifi', icon: Wifi, label: 'Wifi' },
  { name: 'hard-drive', icon: HardDrive, label: 'Storage' },
  { name: 'phone', icon: Phone, label: 'Phone' },
  { name: 'file-text', icon: FileText, label: 'Document' },
  { name: 'router', icon: Router, label: 'Router' },
  { name: 'mail', icon: Mail, label: 'Mail' },
  { name: 'layers', icon: Layers, label: 'Layers' },
  { name: 'lock', icon: Lock, label: 'Lock' },
  { name: 'building', icon: Building2, label: 'Building' },
  { name: 'settings', icon: Settings, label: 'Settings' },
];

// Available colors
const AVAILABLE_COLORS = [
  { name: 'neutral', className: 'bg-neutral-500' },
  { name: 'red', className: 'bg-red-500' },
  { name: 'orange', className: 'bg-orange-500' },
  { name: 'amber', className: 'bg-amber-500' },
  { name: 'yellow', className: 'bg-yellow-500' },
  { name: 'green', className: 'bg-green-500' },
  { name: 'teal', className: 'bg-teal-500' },
  { name: 'cyan', className: 'bg-cyan-500' },
  { name: 'blue', className: 'bg-blue-500' },
  { name: 'indigo', className: 'bg-indigo-500' },
  { name: 'purple', className: 'bg-purple-500' },
  { name: 'pink', className: 'bg-pink-500' },
];

// Field types
const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'url', label: 'URL' },
  { value: 'email', label: 'Email' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'select', label: 'Dropdown' },
];

// Get icon component by name
function getIconComponent(iconName) {
  const found = AVAILABLE_ICONS.find(i => i.name === iconName);
  return found?.icon || Box;
}

// Get color class
function getColorClass(colorName) {
  const found = AVAILABLE_COLORS.find(c => c.name === colorName);
  return found?.className || 'bg-neutral-500';
}

// Field editor component
function FieldEditor({ field, index, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const [expanded, setExpanded] = useState(true);

  const updateField = (key, value) => {
    onChange({ ...field, [key]: value });
  };

  return (
    <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800">
      <div className="flex items-center gap-2 p-3 border-b border-neutral-200 dark:border-neutral-700">
        <GripVertical className="w-4 h-4 text-neutral-400 cursor-grab" />
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex-1 flex items-center gap-2 text-left"
        >
          <span className="font-medium text-neutral-900 dark:text-white">
            {field.label || field.name || `Field ${index + 1}`}
          </span>
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            ({field.type || 'text'})
          </span>
          {field.required && (
            <span className="text-xs text-red-500">*</span>
          )}
        </button>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={isFirst}
            className="p-1 text-neutral-400 hover:text-neutral-600 disabled:opacity-30"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={isLast}
            className="p-1 text-neutral-400 hover:text-neutral-600 disabled:opacity-30"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="p-1 text-red-400 hover:text-red-600"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Field Name (key)
              </label>
              <input
                type="text"
                value={field.name || ''}
                onChange={(e) => updateField('name', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                placeholder="field_name"
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Display Label
              </label>
              <input
                type="text"
                value={field.label || ''}
                onChange={(e) => updateField('label', e.target.value)}
                placeholder="Field Label"
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Field Type
              </label>
              <select
                value={field.type || 'text'}
                onChange={(e) => updateField('type', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
              >
                {FIELD_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Hint Text
              </label>
              <input
                type="text"
                value={field.hint || ''}
                onChange={(e) => updateField('hint', e.target.value)}
                placeholder="Optional help text"
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
              />
            </div>
          </div>

          {field.type === 'select' && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Options (one per line)
              </label>
              <textarea
                value={(field.options || []).join('\n')}
                onChange={(e) => updateField('options', e.target.value.split('\n').filter(o => o.trim()))}
                rows={3}
                placeholder="Option 1&#10;Option 2&#10;Option 3"
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white font-mono text-sm"
              />
            </div>
          )}

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={field.required || false}
                onChange={(e) => updateField('required', e.target.checked)}
                className="w-4 h-4 text-brand-600 rounded"
              />
              <span className="text-sm text-neutral-700 dark:text-neutral-300">Required</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={field.showInList || false}
                onChange={(e) => updateField('showInList', e.target.checked)}
                className="w-4 h-4 text-brand-600 rounded"
              />
              <span className="text-sm text-neutral-700 dark:text-neutral-300">Show in list view</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

// Icon picker component
function IconPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);

  const SelectedIcon = getIconComponent(value);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700"
      >
        <SelectedIcon className="w-5 h-5 text-neutral-700 dark:text-neutral-300" />
        <ChevronDown className="w-4 h-4 text-neutral-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-1 p-2 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg z-20 grid grid-cols-4 gap-1">
            {AVAILABLE_ICONS.map(({ name, icon: Icon }) => (
              <button
                key={name}
                type="button"
                onClick={() => { onChange(name); setOpen(false); }}
                className={`p-2 rounded hover:bg-neutral-100 dark:hover:bg-neutral-700 ${value === name ? 'bg-brand-100 dark:bg-brand-900/30' : ''}`}
              >
                <Icon className="w-5 h-5 text-neutral-700 dark:text-neutral-300" />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Color picker component
function ColorPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700"
      >
        <div className={`w-5 h-5 rounded ${getColorClass(value)}`} />
        <ChevronDown className="w-4 h-4 text-neutral-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-1 p-2 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg z-20 grid grid-cols-4 gap-1">
            {AVAILABLE_COLORS.map(({ name, className }) => (
              <button
                key={name}
                type="button"
                onClick={() => { onChange(name); setOpen(false); }}
                className={`p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-700 ${value === name ? 'ring-2 ring-brand-500' : ''}`}
              >
                <div className={`w-6 h-6 rounded ${className}`} />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Asset type form modal
function AssetTypeModal({ type, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: type?.name || '',
    slug: type?.slug || '',
    description: type?.description || '',
    icon: type?.icon || 'box',
    color: type?.color || 'neutral',
    fieldSchema: type?.field_schema || [],
  });
  const [errors, setErrors] = useState({});

  const isEditing = !!type;

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.slug.trim()) newErrors.slug = 'Slug is required';
    if (!/^[a-z0-9-]+$/.test(formData.slug)) newErrors.slug = 'Slug must be lowercase letters, numbers, and hyphens only';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave({
      name: formData.name,
      slug: formData.slug,
      description: formData.description,
      icon: formData.icon,
      color: formData.color,
      fieldSchema: formData.fieldSchema,
    });
  };

  const generateSlug = () => {
    const slug = formData.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    setFormData({ ...formData, slug });
  };

  const addField = () => {
    setFormData({
      ...formData,
      fieldSchema: [
        ...formData.fieldSchema,
        { name: '', label: '', type: 'text', required: false, showInList: false }
      ]
    });
  };

  const updateField = (index, field) => {
    const newFields = [...formData.fieldSchema];
    newFields[index] = field;
    setFormData({ ...formData, fieldSchema: newFields });
  };

  const removeField = (index) => {
    const newFields = formData.fieldSchema.filter((_, i) => i !== index);
    setFormData({ ...formData, fieldSchema: newFields });
  };

  const moveField = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= formData.fieldSchema.length) return;

    const newFields = [...formData.fieldSchema];
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    setFormData({ ...formData, fieldSchema: newFields });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
            {isEditing ? 'Edit Asset Type' : 'Create Asset Type'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-4 space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                Basic Information
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    onBlur={() => !formData.slug && generateSlug()}
                    placeholder="e.g., Active Directory"
                    className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white ${
                      errors.name ? 'border-red-500' : 'border-neutral-300 dark:border-neutral-600'
                    }`}
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Slug * <span className="text-neutral-400 font-normal">(URL-friendly)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase() })}
                    placeholder="e.g., active-directory"
                    disabled={isEditing}
                    className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white ${
                      errors.slug ? 'border-red-500' : 'border-neutral-300 dark:border-neutral-600'
                    } ${isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                  />
                  {errors.slug && <p className="mt-1 text-sm text-red-500">{errors.slug}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  placeholder="Brief description of this asset type"
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
                />
              </div>

              <div className="flex items-center gap-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Icon
                  </label>
                  <IconPicker value={formData.icon} onChange={(icon) => setFormData({ ...formData, icon })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Color
                  </label>
                  <ColorPicker value={formData.color} onChange={(color) => setFormData({ ...formData, color })} />
                </div>
              </div>
            </div>

            {/* Field Schema */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                  Fields ({formData.fieldSchema.length})
                </h3>
                <button
                  type="button"
                  onClick={addField}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-brand-600 hover:text-brand-700 hover:bg-brand-50 dark:hover:bg-brand-900/20 rounded-lg"
                >
                  <Plus className="w-4 h-4" />
                  Add Field
                </button>
              </div>

              {formData.fieldSchema.length === 0 ? (
                <div className="text-center py-8 text-neutral-500 dark:text-neutral-400 bg-neutral-50 dark:bg-neutral-900/50 rounded-lg">
                  <Box className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No fields defined yet</p>
                  <button
                    type="button"
                    onClick={addField}
                    className="mt-2 text-brand-600 hover:text-brand-700"
                  >
                    Add your first field
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.fieldSchema.map((field, index) => (
                    <FieldEditor
                      key={index}
                      field={field}
                      index={index}
                      onChange={(f) => updateField(index, f)}
                      onRemove={() => removeField(index)}
                      onMoveUp={() => moveField(index, -1)}
                      onMoveDown={() => moveField(index, 1)}
                      isFirst={index === 0}
                      isLast={index === formData.fieldSchema.length - 1}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 p-4 border-t border-neutral-200 dark:border-neutral-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700"
            >
              <Check className="w-4 h-4" />
              {isEditing ? 'Update' : 'Create'} Asset Type
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Delete confirmation modal
function DeleteModal({ type, onClose, onConfirm }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
              <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
                Delete Asset Type
              </h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                This action cannot be undone
              </p>
            </div>
          </div>
          <p className="text-neutral-700 dark:text-neutral-300 mb-6">
            Are you sure you want to delete <strong>{type.name}</strong>?
            {type.asset_count > 0 && (
              <span className="block mt-2 text-red-600 dark:text-red-400">
                This type has {type.asset_count} assets and cannot be deleted.
              </span>
            )}
          </p>
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={type.asset_count > 0}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Template card for library
function TemplateCard({ template, onPreview, onImport, isImported }) {
  const Icon = getIconComponent(template.icon);

  return (
    <div className={`p-4 border rounded-lg transition-colors ${
      isImported
        ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
        : 'border-neutral-200 dark:border-neutral-700 hover:border-brand-300 dark:hover:border-brand-600'
    }`}>
      <div className="flex items-start gap-3 mb-3">
        <div className={`p-2 rounded-lg ${getColorClass(template.color)} bg-opacity-20`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-neutral-900 dark:text-white">{template.name}</h3>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 line-clamp-2">{template.description}</p>
        </div>
      </div>
      <div className="text-xs text-neutral-400 mb-3">
        {template.fieldSchema.length} fields
      </div>
      <div className="flex gap-2">
        <button
          onClick={onPreview}
          className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300"
        >
          <Eye className="w-3 h-3" />
          Preview
        </button>
        {isImported ? (
          <div className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-sm bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg">
            <Check className="w-3 h-3" />
            Imported
          </div>
        ) : (
          <button
            onClick={onImport}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700"
          >
            <Download className="w-3 h-3" />
            Import
          </button>
        )}
      </div>
    </div>
  );
}

// Template preview panel
function TemplatePreviewPanel({ template, onClose, onImport, isImported }) {
  return (
    <div className="border-t border-neutral-200 dark:border-neutral-700 p-4 bg-neutral-50 dark:bg-neutral-900">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-neutral-900 dark:text-white">{template.name}</h3>
        <button onClick={onClose} className="text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300">
          <X className="w-4 h-4" />
        </button>
      </div>
      <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">{template.description}</p>
      <div className="mb-4">
        <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Fields:</h4>
        <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
          {template.fieldSchema.map(field => (
            <div key={field.name} className="text-sm">
              <span className="text-neutral-900 dark:text-white">{field.label}</span>
              <span className="text-neutral-500 ml-1">({field.type})</span>
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </div>
          ))}
        </div>
      </div>
      {isImported ? (
        <div className="w-full py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg text-center flex items-center justify-center gap-2">
          <Check className="w-4 h-4" />
          Already Imported
        </div>
      ) : (
        <button
          onClick={onImport}
          className="w-full py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 flex items-center justify-center gap-2"
        >
          <Download className="w-4 h-4" />
          Import Template
        </button>
      )}
    </div>
  );
}

// Template Library Modal
function TemplateLibraryModal({ existingSlugs, onClose, onImport }) {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const filteredTemplates = selectedCategory === 'all'
    ? ASSET_TEMPLATES
    : ASSET_TEMPLATES.filter(t => t.category === selectedCategory);

  const handleImport = (template) => {
    onImport(template);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl w-full max-w-4xl my-8 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
              Template Library
            </h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Import pre-configured asset types for common IT infrastructure
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex" style={{ height: '60vh' }}>
          {/* Category sidebar */}
          <div className="w-48 border-r border-neutral-200 dark:border-neutral-700 p-4 flex-shrink-0">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`w-full text-left px-3 py-2 rounded-lg mb-1 text-sm ${
                selectedCategory === 'all'
                  ? 'bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-400'
                  : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700'
              }`}
            >
              All Templates ({ASSET_TEMPLATES.length})
            </button>
            {ASSET_TEMPLATE_CATEGORIES.map(cat => {
              const count = ASSET_TEMPLATES.filter(t => t.category === cat.id).length;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg mb-1 text-sm ${
                    selectedCategory === cat.id
                      ? 'bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-400'
                      : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                  }`}
                >
                  {cat.name} ({count})
                </button>
              );
            })}
          </div>

          {/* Template grid */}
          <div className="flex-1 p-4 overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              {filteredTemplates.map(template => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  isImported={existingSlugs.includes(template.slug)}
                  onPreview={() => setPreviewTemplate(template)}
                  onImport={() => handleImport(template)}
                />
              ))}
            </div>
            {filteredTemplates.length === 0 && (
              <div className="text-center py-12 text-neutral-500 dark:text-neutral-400">
                No templates in this category
              </div>
            )}
          </div>
        </div>

        {/* Preview panel */}
        {previewTemplate && (
          <TemplatePreviewPanel
            template={previewTemplate}
            isImported={existingSlugs.includes(previewTemplate.slug)}
            onClose={() => setPreviewTemplate(null)}
            onImport={() => { handleImport(previewTemplate); setPreviewTemplate(null); }}
          />
        )}
      </div>
    </div>
  );
}

// Main page component
export default function AdminAssetTypesPage() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [deletingType, setDeletingType] = useState(null);

  // Fetch asset types
  const { data, isLoading } = useQuery({
    queryKey: ['documentation', 'asset-types'],
    queryFn: () => documentationAPI.listAssetTypes(),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => documentationAPI.createAssetType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation', 'asset-types'] });
      setShowModal(false);
      setShowTemplateLibrary(false);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => documentationAPI.updateAssetType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation', 'asset-types'] });
      setEditingType(null);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => documentationAPI.deleteAssetType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation', 'asset-types'] });
      setDeletingType(null);
    },
  });

  // Clone mutation
  const cloneMutation = useMutation({
    mutationFn: (id) => documentationAPI.cloneAssetType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation', 'asset-types'] });
    },
  });

  const types = data?.data?.types || [];
  const existingSlugs = types.map(t => t.slug);

  // Import template handler
  const handleImportTemplate = (template) => {
    createMutation.mutate({
      name: template.name,
      slug: template.slug,
      description: template.description,
      icon: template.icon,
      color: template.color,
      fieldSchema: template.fieldSchema,
    });
  };

  const handleSave = (formData) => {
    if (editingType) {
      updateMutation.mutate({ id: editingType.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (type) => {
    setEditingType(type);
  };

  const handleDelete = (type) => {
    setDeletingType(type);
  };

  const confirmDelete = () => {
    if (deletingType) {
      deleteMutation.mutate(deletingType.id);
    }
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Asset Types</h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            Manage custom asset type templates for documentation
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowTemplateLibrary(true)}
            className="flex items-center gap-2 px-4 py-2 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700"
          >
            <Download className="w-5 h-5" />
            Import Template
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700"
          >
            <Plus className="w-5 h-5" />
            New Asset Type
          </button>
        </div>
      </div>

      {/* Types list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
        </div>
      ) : types.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700">
          <Box className="w-12 h-12 mx-auto mb-4 text-neutral-400" />
          <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">No asset types yet</h3>
          <p className="text-neutral-600 dark:text-neutral-400 mb-4">
            Create your first asset type to start documenting custom assets
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700"
          >
            <Plus className="w-5 h-5" />
            Create Asset Type
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {types.map(type => {
            const Icon = getIconComponent(type.icon);
            return (
              <div
                key={type.id}
                className="flex items-center gap-4 p-4 bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700"
              >
                <div className={`p-3 rounded-lg ${getColorClass(type.color)} bg-opacity-20`}>
                  <Icon className={`w-6 h-6 text-${type.color}-600 dark:text-${type.color}-400`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-neutral-900 dark:text-white">{type.name}</h3>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
                    {type.description || 'No description'}
                  </p>
                </div>
                <div className="flex items-center gap-4 text-sm text-neutral-500 dark:text-neutral-400">
                  <span>{type.field_schema?.length || 0} fields</span>
                  <span>{type.asset_count || 0} assets</span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => cloneMutation.mutate(type.id)}
                    disabled={cloneMutation.isPending}
                    className="p-2 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg disabled:opacity-50"
                    title="Clone"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(type)}
                    className="p-2 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(type)}
                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit modal */}
      {(showModal || editingType) && (
        <AssetTypeModal
          type={editingType}
          onClose={() => { setShowModal(false); setEditingType(null); }}
          onSave={handleSave}
        />
      )}

      {/* Delete confirmation */}
      {deletingType && (
        <DeleteModal
          type={deletingType}
          onClose={() => setDeletingType(null)}
          onConfirm={confirmDelete}
        />
      )}

      {/* Template Library */}
      {showTemplateLibrary && (
        <TemplateLibraryModal
          existingSlugs={existingSlugs}
          onClose={() => setShowTemplateLibrary(false)}
          onImport={handleImportTemplate}
        />
      )}
    </div>
  );
}
